import React from 'react'
import { Box, Typography, TextField, Button, CircularProgress, IconButton, InputAdornment } from '@mui/material'
import Link from '@mui/material/Link';
import { RightArrow } from '../assets/icons';
import { LogoDarkMode } from '../assets/images';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useState } from 'react';
import AuthService from '../services/auth.service';
import { useNavigate } from 'react-router-dom';
import { wrap } from 'module';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0); // 0 for email, 1 for verification
    const [email, setEmail] = useState('');
    const [formData, setFormData] = useState({
        code: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [codeError, setCodeError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validatePassword = (password: string) => {
        const minLength = 8;
        const hasNumber = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < minLength) {
            return 'Password must be at least 8 characters long';
        }
        if (!hasNumber) {
            return 'Password must contain at least one number';
        }
        if (!hasSpecialChar) {
            return 'Password must contain at least one special character';
        }
        return '';
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await AuthService.forgotPassword(email);
            setStep(1); // Move to verification step
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerificationSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setCodeError('');
        setError('');

        const passwordValidationError = validatePassword(formData.password);
        if (passwordValidationError) {
            setError(passwordValidationError);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            await AuthService.confirmForgotPassword(
                email,
                formData.code,
                formData.password
            );
            window.location.href = '/login';
        } catch (err: any) {
            setCodeError(err.message);
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Add password validation on input change
        if (name === 'password') {
            setPasswordError(validatePassword(value));
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // Check password match on blur for confirm password field
        if (name === 'confirmPassword') {
            if (value !== formData.password) {
                setError('Passwords do not match');
            } else {
                setError('');
            }
        }
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            width: '100vw',
            background: `
                linear-gradient(180deg, #010007 26.92%, #AC8DFF 100%),
                linear-gradient(180deg, #FFFFEF 0%, #FFF 100%)
            `,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Inter',
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: { xs: "16px 24px", sm: "24px 32px"},
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img src={LogoDarkMode} alt="logo" style={{ height: 24, cursor: 'pointer', maxWidth: '100%' }} onClick={() => window.open('https://easywebinar.com/', '_blank')} />
                </Box>
                <Button
                    variant="contained"
                    onClick={() => navigate('/login')}
                    sx={{
                        bgcolor: '#6066E9',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: 14,
                        py: 0.5,
                        px: 1,
                        minHeight: 'auto',
                        transition: 'color 0.2s',
                        '&:hover': {
                            bgcolor: '#4B4EB5',
                            boxShadow: 'none !important',
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box component="span" sx={{ transform: 'rotate(180deg)', display: 'inline-flex', alignItems: 'center' }}>
                        <Box component="span" display="flex" alignItems="center" sx={{ '& path': { stroke: '#fff ' }, '& svg': { color: '#fff' } }}>
                            <RightArrow />
                        </Box>
                    </Box>
                    Back to login
                </Button>
            </Box>

            {/* Main Content */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                minHeight: { xs: 'calc(100vh - 120px)', sm: 'calc(100vh - 100px)', md: 'calc(100vh - 80px)' },
            }}>
                <Box sx={{
                    bgcolor: '#ffffff',
                    borderRadius: '18px',
                    boxShadow: '0 4px 32px 0 rgba(0,0,0,0.15)',
                    width: { xs: '98%', sm: 600 },
                    maxWidth: 600,
                    p: { xs: '24px 16px', sm: 4, lg: 6 },
                    mx: { xs: 1, sm: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    border: '1.5px solid #e5e7eb',
                    // Autofill override for all inputs inside the card
                    '& input:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
                        WebkitTextFillColor: '#1f2937',
                        caretColor: '#1f2937',
                        transition: 'background-color 5000s ease-in-out 0s',
                    },
                    '& input:-webkit-autofill:focus': {
                        WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
                        WebkitTextFillColor: '#1f2937',
                    },
                    '& input:-webkit-autofill:hover': {
                        WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
                        WebkitTextFillColor: '#1f2937',
                    },
                }}>
                    {step === 0 ? (
                        // Email Step
                        <>
                            {/* Welcome Text */}
                            <Typography color="#1f2937" fontWeight={700} textAlign="center" mb={0.5}
                                fontSize={{ xs: 22, sm: 26, md: 28 }}
                                sx={{ lineHeight: { xs: '32px', sm: '36px', md: '40px' } }}>
                                Reset your password
                            </Typography>
                            <Typography color="#6b7280" textAlign="center" mb={4}
                                fontSize={{ xs: 13, sm: 15 }}
                                sx={{ lineHeight: { xs: '20px', sm: '22px' } }}>
                                Enter your email address to reset your password
                            </Typography>

                            {/* Form */}
                            <Box component="form" onSubmit={handleEmailSubmit} width="100%">
                                <TextField
                                    variant="outlined"
                                    label="Email address"
                                    name="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    error={!!error}
                                    helperText={error}
                                    fullWidth
                                    autoComplete="email"
                                    sx={{
                                        mb: 4,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            bgcolor: '#ffffff',
                                            color: '#1f2937',
                                            transition: 'border-color 0.2s',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#6b7280',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#6066E9',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d1d5db',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6066E9',
                                        },
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6066E9',
                                        },
                                        input: { color: '#1f2937' },
                                    }}
                                    InputLabelProps={{ style: { color: '#6b7280' } }}
                                />

                                <Button
                                    variant="contained"
                                    type="submit"
                                    disabled={isLoading}
                                    fullWidth
                                    sx={{
                                        bgcolor: '#6066E9',
                                        color: '#fff',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: 17,
                                        py: 1.3,
                                        mb: 3,
                                        textTransform: 'none',
                                        boxShadow: 'none',
                                        '&:hover': { bgcolor: '#4B4EB5' },
                                    }}
                                >
                                    {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Send Reset Code'}
                                </Button>
                            </Box>
                        </>
                    ) : (
                        // Verification Step
                        <>
                            {/* Welcome Text */}
                            <Typography color="#1f2937" fontWeight={700} textAlign="center" mb={0.5}
                                fontSize={{ xs: 22, sm: 26, md: 28 }}
                                sx={{ lineHeight: { xs: '32px', sm: '36px', md: '40px' } }}>
                                Enter verification code
                            </Typography>
                            <Typography color="#6b7280" textAlign="center" mb={4}
                                fontSize={{ xs: 13, sm: 15 }}
                                sx={{ lineHeight: { xs: '20px', sm: '22px' } }}>
                                Enter the 6-digit code you received on your email
                            </Typography>

                            {/* Form */}
                            <Box component="form" onSubmit={handleVerificationSubmit} width="100%">
                                <TextField
                                    variant="outlined"
                                    label="Enter Code"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleInputChange}
                                    error={!!codeError}
                                    helperText={codeError}
                                    fullWidth
                                    autoComplete="off"
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            bgcolor: '#ffffff',
                                            color: '#1f2937',
                                            transition: 'border-color 0.2s',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#6b7280',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#6066E9',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d1d5db',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6066E9',
                                        },
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6066E9',
                                        },
                                        input: { color: '#1f2937' },
                                    }}
                                    InputLabelProps={{ style: { color: '#6b7280' } }}
                                />

                                <TextField
                                    variant="outlined"
                                    label="New Password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    error={!!passwordError}
                                    helperText={passwordError}
                                    fullWidth
                                    autoComplete="new-password"
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            bgcolor: '#ffffff',
                                            color: '#1f2937',
                                            transition: 'border-color 0.2s',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#6b7280',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#6066E9',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d1d5db',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6066E9',
                                        },
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6066E9',
                                        },
                                        input: { color: '#1f2937' },
                                    }}
                                    InputLabelProps={{ style: { color: '#6b7280' } }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    onMouseUp={handleMouseUpPassword}
                                                    edge="end"
                                                    sx={{ color: '#6b7280' }}
                                                >
                                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    variant="outlined"
                                    label="Confirm New Password"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    error={!!error && formData.confirmPassword !== ''}
                                    helperText={formData.confirmPassword !== '' ? error : ''}
                                    fullWidth
                                    autoComplete="new-password"
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            bgcolor: '#ffffff',
                                            color: '#1f2937',
                                            transition: 'border-color 0.2s',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#6b7280',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#6066E9',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d1d5db',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6066E9',
                                        },
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6066E9',
                                        },
                                        input: { color: '#1f2937' },
                                    }}
                                    InputLabelProps={{ style: { color: '#6b7280' } }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleClickShowConfirmPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    onMouseUp={handleMouseUpPassword}
                                                    edge="end"
                                                    sx={{ color: '#6b7280' }}
                                                >
                                                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Resend Code */}
                                <Box display="flex" justifyContent="center" mb={3}>
                                    <Typography color="#6b7280" fontSize={14}>
                                        Didn't receive the code?{' '}
                                        <Box
                                            component="span"
                                            color="#6066E9"
                                            sx={{
                                                cursor: 'pointer',
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                            onClick={() => AuthService.forgotPassword(email)}
                                        >
                                            Resend Code
                                        </Box>
                                    </Typography>
                                </Box>

                                <Button
                                    variant="contained"
                                    type="submit"
                                    disabled={isLoading}
                                    fullWidth
                                    sx={{
                                        bgcolor: '#6066E9',
                                        color: '#fff',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: 17,
                                        py: 1.3,
                                        mb: 3,
                                        textTransform: 'none',
                                        boxShadow: 'none',
                                        '&:hover': { bgcolor: '#4B4EB5' },
                                    }}
                                >
                                    {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Reset Password'}
                                </Button>
                            </Box>
                        </>
                    )}

                    {/* Footer */}
                    <Box width="100%" mt={2}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                            <Typography color="#9ca3af" sx={{ fontSize: { xs: 11, sm: 13 } }}>
                                &copy; 2025 All Rights Reserved
                            </Typography>
                            <Box display="flex" gap={2}>
                                <Link href="https://easywebinar.com/terms-conditions/" target="_blank" rel="noopener noreferrer" sx={{ color: '#9ca3af', fontSize: { xs: 11, sm: 13 }, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Terms of Service</Link>
                                <Link href="https://easywebinar.com/privacy/" target="_blank" rel="noopener noreferrer" sx={{ color: '#9ca3af', fontSize: { xs: 11, sm: 13 }, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Privacy Policy</Link>
                                <Link href="https://easywebinar.com/cookies/" target="_blank" rel="noopener noreferrer" sx={{ color: '#9ca3af', fontSize: { xs: 11, sm: 13 }, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Security</Link>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default ForgotPassword